import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ModerationFilesManagement } from '../../../../models';

import { Card, ContentCard, FilterSetController, WeezHeader, Table, Status, Filter, Icon } from '@weezevent/nacre';
import { attendeeStatusFilterOptions } from '../../../../constants/moderation';
import LoadingTable from '../../../../components/loadingTable';
import ModerationPaginator from '../../components/moderationPaginator';
import { matchSearch } from '../../../../utils/tools';

const ImportDetail = ({ organization, flowId, importId }) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [search, setSearch] = React.useState('');
    const [pageSize] = React.useState(50);
    const [initialFilters] = React.useState({});
    const [filters, setFilters] = React.useState(initialFilters);
    const [currentPage, setCurrentPage] = React.useState(0);

    const [file, loadingFile] = ModerationFilesManagement.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            id: importId,
            launch: importId,
            upload: true,
            cache: false,
            allow_cache: false
        },
        [(organization, flowId, importId)]
    );

    const filterStatuses = React.useCallback(
        rows => {
            const _filters = Object.entries(filters).map(([key, value]) => ({ [key]: Array.from(value) }));
            return rows.filter(row =>
                _filters.every(filter => {
                    const [key, values] = Object.entries(filter)[0];
                    return values.includes(row[key].name);
                })
            );
        },
        [filters]
    );

    const filteredRows = React.useMemo(() => {
        if (!file || !file.rows.length || loadingFile) {
            return [];
        }

        const offset = currentPage * pageSize;
        const limit = offset + pageSize;
        const rows = filterStatuses(file?.rows.slice(offset, limit));

        return rows.filter(row => matchSearch([row.attendee?.attendee_first_name, row.attendee?.attendee_last_name], search));
    }, [file, loadingFile, search, currentPage, pageSize, filterStatuses]);

    return (
        <>
            <WeezHeader
                title={t('ticket.moderation.filesManagement.imports.header')}
                backLink={{
                    onClick: () => {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/files/imports`);
                    }
                }}
            >
                {file?.filename}
            </WeezHeader>
            <Card>
                <ContentCard>
                    <FilterSetController
                        total={file?.rows.length}
                        isLoadingTotal={loadingFile}
                        labelTotal={t('ticket.moderation.attendees.table.countTotal', { count: file?.rows.length })}
                        onChange={values => setFilters(values)}
                        initialValues={filters}
                        defaultValues={initialFilters}
                        onReset={() => {}}
                        search={{
                            onChange: (_, value) => setSearch(value),
                            handleReset: () => setSearch(''),
                            placeholder: t('ticket.moderation.common.searchPlaceholder'),
                            value: search
                        }}
                    >
                        <Filter name="source_status" labelName={t('ticket.moderation.filesManagement.imports.source')} multiple options={attendeeStatusFilterOptions()} index="1" />
                        <Filter
                            name="received_status"
                            labelName={t('ticket.moderation.filesManagement.imports.destination')}
                            multiple
                            options={attendeeStatusFilterOptions()}
                            index="2"
                        />
                    </FilterSetController>
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.filesManagement.imports.line')}</th>
                            <th>{t('ticket.moderation.filesManagement.imports.attendee')}</th>
                            <th>{t('ticket.moderation.filesManagement.imports.source')}</th>
                            <th>{t('ticket.moderation.filesManagement.imports.destination')}</th>
                            <th>{t('ticket.moderation.filesManagement.imports.import')}</th>
                        </Table.Tr>
                    </Table.Header>

                    <Table.TBody>
                        {filteredRows.map((row, index) => (
                            <ImportRow key={index} row={row} index={index} />
                        ))}
                    </Table.TBody>
                </Table>
                <LoadingTable loading={loadingFile} height="30vh" />
                {!loadingFile && !filteredRows.length && (
                    <Card.EmptyState title={t('ticket.moderation.attendees.emptyTitle')} subtitle={t('ticket.moderation.attendees.emptySubtitle')} icon={<Icon name="list" />} />
                )}
                <ModerationPaginator currentPage={currentPage} setCurrentPage={setCurrentPage} totalCount={file?.rows.length} pageSize={pageSize} />
            </Card>
        </>
    );
};

export default ImportDetail;

const ImportRow = ({ row, index }) => {
    const { t } = useTranslation();
    const { attendee, source_status, received_status } = row;

    const status = React.useMemo(() => {
        let values = {
            label: 'error',
            color: 'red'
        };
        if (!row.error) {
            values = {
                label: row.ignored ? 'ignored' : 'done',
                color: row.ignored ? null : 'green'
            };
        }
        return values;
    }, [row]);

    return (
        <Table.Tr>
            <Table.Td>
                <span>{index + 1}</span>
            </Table.Td>
            <Table.Td>
                <span>{`${attendee?.attendee_last_name.toUpperCase()} ${attendee?.attendee_first_name}`}</span>
            </Table.Td>
            <Table.Td>
                <span>{source_status?.name}</span>
            </Table.Td>
            <Table.Td>
                <span>{received_status?.name}</span>
            </Table.Td>
            <Table.Td>
                <Status responsive text={t(`ticket.moderation.filesManagement.status.${status.label}`)} color={status.color} />
            </Table.Td>
        </Table.Tr>
    );
};
